import { useCallback, useEffect, useState } from 'react'
import { getMfaStatus } from '../security/mfa.service'
import type { MfaStatus } from '../security/mfa.types'
import { hasFreshMfaSessionBridge } from '../security/mfaBridge.utils'

const MFA_BRIDGE_RETRY_DELAYS_MS = [100, 200, 300, 500, 800] as const

interface UseSuperadminMfaGateOptions {
  /** Wacht kort op AAL2 als parent-tab recent MFA bevestigde (view-as in nieuw tabblad). */
  allowBridgeRetry?: boolean
}

async function loadMfaStatusWithBridgeRetry (
  allowBridgeRetry: boolean
): Promise<MfaStatus> {
  let status = await getMfaStatus()

  if (
    allowBridgeRetry &&
    !status.isSessionElevated &&
    status.hasVerifiedTotp &&
    hasFreshMfaSessionBridge()
  ) {
    for (const delayMs of MFA_BRIDGE_RETRY_DELAYS_MS) {
      await new Promise<void>((resolve) => {
        window.setTimeout(resolve, delayMs)
      })
      status = await getMfaStatus()
      if (status.isSessionElevated) break
    }
  }

  return status
}

export function useSuperadminMfaGate (options?: UseSuperadminMfaGateOptions) {
  const allowBridgeRetry = options?.allowBridgeRetry ?? false
  const [status, setStatus] = useState<MfaStatus | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const reload = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const next = await loadMfaStatusWithBridgeRetry(allowBridgeRetry)
      setStatus(next)
    } catch (e) {
      setStatus(null)
      setError(e instanceof Error ? e.message : 'Kon MFA-status niet laden')
    } finally {
      setLoading(false)
    }
  }, [allowBridgeRetry])

  useEffect(() => {
    void reload()
  }, [reload])

  return { status, loading, error, reload }
}
